import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { ErrorBoundaryStore } from './error-boundary.store';
import { isChunkLoadError, recoverFromChunkLoadError } from './chunk-load-error';

/**
 * Known-benign browser conditions that must never trip the full-screen
 * error fallback — they aren't application errors, and treating them as
 * such false-positives the user into "Something went wrong" for something
 * that isn't actually wrong.
 *
 * - "ResizeObserver loop ..." (both the older "...limit exceeded" and
 *   current "...completed with undelivered notifications" wording): fired
 *   by the browser itself when a ResizeObserver callback would trigger more
 *   layout work within the same frame. Browsers throttle this and emit a
 *   warning-level notification, not a real failure — extremely common
 *   (triggered by plenty of ordinary layout, not a specific bug) and
 *   universally treated as a no-op. See
 *   https://github.com/WICG/resize-observer/issues/38. `provideBrowserGlobalErrorListeners()`
 *   forwards this here via `window.onerror` regardless, since browsers
 *   surface it as a real `ErrorEvent` at that level — Angular wraps that
 *   into an `Error` whose `.message` starts with "An ErrorEvent with no
 *   error occurred..." followed by this text, which is why the check below
 *   matches on message *contents* rather than error identity/type.
 */
const IGNORED_ERROR_PATTERNS = [/ResizeObserver loop/i];

/**
 * App-wide `ErrorHandler` — Angular's structural equivalent of Consumer's
 * top-level `ErrorBoundary.tsx` (which wraps the entire `<AppRoutes/>` tree,
 * i.e. one boundary for the whole app, not per-subtree). Angular doesn't
 * have React's per-subtree error-boundary primitive, so a single global
 * handler feeding a shared store — rendered by the root `AppComponent` — is
 * the correct 1:1 structural match rather than a downgrade.
 *
 * Registered via `{ provide: ErrorHandler, useClass: GlobalErrorHandler }`
 * in app.config.ts.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // ErrorHandler is constructed before DI is fully settled in some app-init
  // orders, so resolve the store lazily via Injector rather than constructor injection.
  private injector = inject(Injector);

  handleError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error ?? '');

    if (IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
      // Still logged for visibility, but deliberately not reported to
      // ErrorBoundaryStore — see IGNORED_ERROR_PATTERNS' doc comment.
      console.warn('[GlobalErrorHandler] ignored benign error', error);
      return;
    }

    // A failed lazy-chunk / dynamic-import load is a stale-version condition
    // (old build requesting a hashed chunk a newer deploy removed, or the brief
    // pre-service-worker window), not an app bug — recover with a reload onto
    // the fresh build instead of showing the fatal fallback. See chunk-load-error.ts.
    if (isChunkLoadError(message)) {
      console.warn('[GlobalErrorHandler] chunk/dynamic-import load failed — recovering without the error screen', error);
      // recoverFromChunkLoadError navigates away (reload/hard-reload) once it
      // decides to recover; only report the fatal fallback if it couldn't.
      void recoverFromChunkLoadError(error).then((recovered) => {
        if (!recovered) this.report(error);
      });
      return;
    }

    console.error('[GlobalErrorHandler]', error);
    this.report(error);
  }

  private report(error: unknown): void {
    try {
      this.injector.get(ErrorBoundaryStore).report(error);
    } catch {
      // Store not available yet (very early boot error) — already logged.
    }
  }
}
